'use client'

import type { ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import styles from './auth-shell.module.css'

interface AuthShellProps {
  children: ReactNode
}

// Single-family rule (DESIGN.md → Typography): Geist for headings and body.
// The root layout loads Geist on <html>, so var(--font-geist) is available here.
const HEADING_STACK = 'var(--font-geist), ui-sans-serif, system-ui, -apple-system, sans-serif'
const BODY_STACK = 'var(--font-geist), ui-sans-serif, system-ui, -apple-system, sans-serif'

const CURRENT_YEAR = new Date().getFullYear()

// The quote is an attributed English statement by Jack Zoppa. Intentionally not
// translated — translating a direct attributed quote would undermine its
// authenticity. Only the role line is localized.
const QUOTE_BODY =
  '\u201CThe bet is agents. Agents are going to do 1 major thing for us \u2014 increase hospitality output.\u201D'
const QUOTE_NAME = 'Jack Zoppa'

export function AuthShell({ children }: AuthShellProps) {
  const t = useTranslations('Auth')
  return (
    <div
      style={{
        minHeight: '100dvh',
        width: '100%',
        backgroundColor: 'hsl(var(--background))',
      }}
    >
      <div className={styles.layout}>
        <section className={styles.formColumn}>
          <header className={styles.formHeader}>
            {/* Static shell assets stay unoptimized so SSR and client URLs remain identical. */}
            <img
              src="/trellis-wordmark.svg"
              alt="Trellis"
              style={{ display: 'block', height: '25px', width: 'auto', flexShrink: 0 }}
            />
          </header>
          <main className={styles.formBody}>{children}</main>
          <footer className={styles.formFooter} style={{ fontFamily: BODY_STACK }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                paddingTop: '16px',
                borderTop: '1px solid var(--hairline-faint)',
                fontSize: '12px',
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              <span>© {CURRENT_YEAR} Trellis Tech Inc.</span>
              <div
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '14px' }}
              >
                <a
                  href="https://trellistech.com/privacy"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {t('privacy')}
                </a>
                <a
                  href="https://trellistech.com/terms"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {t('terms')}
                </a>
              </div>
            </div>
          </footer>
        </section>
        <aside className={styles.terraceColumn} aria-hidden="true">
          <img
            src="/hero/terrace-v6.webp"
            alt=""
            className={styles.terraceImage}
          />
          <div className={styles.terraceVignette} />
          <div className={styles.quoteBlock}>
            <p
              style={{
                fontFamily: HEADING_STACK,
                fontWeight: 500,
                fontSize: '22px',
                lineHeight: 1.35,
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
                textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                margin: 0,
                maxWidth: '520px',
              }}
            >
              {QUOTE_BODY}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span
                style={{
                  fontFamily: BODY_STACK,
                  fontWeight: 500,
                  fontSize: '13px',
                  color: '#FFFFFF',
                  textShadow: '0 1px 12px rgba(0,0,0,0.4)',
                }}
              >
                {QUOTE_NAME}
              </span>
              <span
                style={{
                  fontFamily: BODY_STACK,
                  fontWeight: 400,
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.78)',
                  textShadow: '0 1px 12px rgba(0,0,0,0.4)',
                }}
              >
                {t('quoteRole')}
              </span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export const authFontStacks = {
  heading: HEADING_STACK,
  body: BODY_STACK,
}
